import { lazy, Suspense, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Sun, Moon, Map, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useMapPreview } from "@/contexts/MapPreviewContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import WelcomePanel from "@/components/WelcomePanel";
import ToolSeoContent from "@/components/seo/ToolSeoContent";
import ConvertSeoContent from "@/components/seo/ConvertSeoContent";
import ToolSkeleton from "@/components/tools/shared/ToolSkeleton";
import ErrorBoundary from "@/components/ErrorBoundary";
import { t, type TranslationKey } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";

const TOOL_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  reproject:  lazy(() => import("./tools/ReprojectTool")),
  converter:  lazy(() => import("./tools/ConverterTool")),
  validate:   lazy(() => import("./tools/ValidatorTool")),
  buffer:     lazy(() => import("./tools/BufferTool")),
  simplify:   lazy(() => import("./tools/SimplifyTool")),
  calculate:  lazy(() => import("./tools/CalculateTool")),
  merge:      lazy(() => import("./tools/MergeTool")),
  clip:       lazy(() => import("./tools/ClipTool")),
  coordinate: lazy(() => import("./tools/CoordinateConverterTool")),
  layout:     lazy(() => import("./tools/LayoutTool")),
  share:      lazy(() => import("./tools/ShareTool")),
};

const TOOL_NAMES: Record<string, string> = {
  reproject:  "Reproject",
  converter:  "Format Converter",
  validate:   "Validate & Fix",
  buffer:     "Buffer Analysis",
  simplify:   "Simplify Geometry",
  calculate:  "Measurements",
  merge:      "Merge Shapefiles",
  clip:       "Clip by Extent",
  coordinate: "Coordinates",
  layout:     "Layout Peta",
  share:      "Share & Bagikan",
};

const MAP_TOOLS = new Set(["reproject", "buffer", "clip", "simplify", "validate", "merge", "share"]);

interface LeftPanelProps {
  activeTool: string | null;
  /** Conversion landing page: pair slug (e.g. "shp-to-kml") or "hub"; null otherwise. */
  convertSlug?: string | null;
  toolKey: number;
  onOpenFAQ: () => void;
  onOpenAbout: () => void;
  onOpenFeedback: () => void;
}

const LeftPanel = ({
  activeTool,
  convertSlug = null,
  toolKey,
  onOpenFAQ,
  onOpenAbout,
  onOpenFeedback,
}: LeftPanelProps) => {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const { locale, setLocale } = useLocale();
  const { geoJSON, mapOpen, setMapOpen } = useMapPreview();
  const [collapsed, setCollapsed] = useState(false);
  const ToolComponent = activeTool ? TOOL_COMPONENTS[activeTool] : null;
  // Show the Map button for any map-capable tool so the feature is discoverable;
  // disable it until the tool has produced a result to preview.
  const isMapTool = !!activeTool && MAP_TOOLS.has(activeTool);
  const mapReady = !!geoJSON;

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 md:h-screen transition-all duration-300 ${
        collapsed ? "w-full md:w-20 overflow-hidden" : "w-full"
      }`}
      style={{ padding: collapsed ? 12 : 20 }}
    >
      {/* ── Brand + theme toggle - one compact row ── */}
      <div className="flex items-start justify-between mb-3">
        {/* Left: title / tagline stacked tightly */}
        {!collapsed && (
          <div>
            {/* Display title. Once a tool is open the brand steps back: the
                wordmark shrinks and the tagline is dropped, handing that
                vertical space to the tool panel — which is what the user
                actually came for. */}
            {/* Not an <h1>: the wordmark repeats on every page and would
                compete with each page's real title for the same signal. */}
            <p
              className="font-black leading-none tracking-tight transition-all duration-300"
              style={{
                fontSize: activeTool ? "clamp(20px, 1.7vw, 26px)" : "clamp(28px, 2.9vw, 44px)",
                letterSpacing: "-0.02em",
              }}
            >
              Shp<span style={{ color: "hsl(var(--brand-strong))" }}>.Aid</span>
            </p>

            {/* Tagline — landing only */}
            {!activeTool && (
              <p
                style={{
                  fontSize: 10,
                  color: "hsl(var(--muted-foreground))",
                  marginTop: 3,
                  lineHeight: 1.3,
                }}
              >
                {t("brand.tagline")}
              </p>
            )}
          </div>
        )}

        {/* Language + theme + collapse toggles */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Collapse sidepanel button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setCollapsed((v) => !v)}
                className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label={collapsed ? "Expand Sidepanel" : "Collapse Sidepanel"}
              >
                {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {collapsed ? "Expand Panel" : "Collapse Panel"}
            </TooltipContent>
          </Tooltip>

          {!collapsed && (
            <>
              {/* Language toggle (EN / ID) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setLocale(locale === "en" ? "id" : "en")}
                    className="h-7 px-2 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em" }}
                    aria-label={t("lang.toggle")}
                  >
                    {locale.toUpperCase()}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {locale === "en" ? t("lang.id") : t("lang.en")}
                </TooltipContent>
              </Tooltip>

              {/* Theme toggle */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                    className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    aria-label={t("theme.toggle")}
                  >
                    {resolvedTheme === "dark" ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {resolvedTheme === "dark" ? t("theme.to.light") : t("theme.to.dark")}
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* ── Tool panel (flex-1 - takes ALL remaining vertical space) ── */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">

        {/* Label row */}
        <div className="flex items-center justify-between mb-1">
          <p
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "hsl(var(--brand-strong))",
            }}
          >
            {activeTool ? t(`tool.${activeTool}` as TranslationKey) : t("panel.tool")}
          </p>

          {isMapTool && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => mapReady && setMapOpen(!mapOpen)}
                  disabled={!mapReady}
                  aria-disabled={!mapReady}
                  className="flex items-center gap-1 transition-opacity"
                  style={{
                    padding: "2px 8px",
                    borderRadius: 6,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    background: mapOpen && mapReady ? "hsl(var(--brand))" : "hsl(var(--accent))",
                    color: mapOpen && mapReady ? "hsl(var(--brand-fg))" : "hsl(var(--muted-foreground))",
                    opacity: mapReady ? 1 : 0.45,
                    cursor: mapReady ? "pointer" : "not-allowed",
                  }}
                >
                  <Map style={{ width: 9, height: 9 }} />
                  {t("panel.map")}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {mapReady
                  ? `${mapOpen ? t("panel.map.hide") : t("panel.map.show")}`
                  : t("panel.map.pending")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Panel rectangle (borderless for a cleaner, merged look) */}
        <div
          className="flex-1 min-h-0 rounded-panel"
          style={{
            border: "none",
            background: activeTool
              ? "hsl(var(--card) / 0.6)"
              : "transparent",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!activeTool ? (
            <WelcomePanel />
          ) : (
            /* Scrollable tool content - scrollbar hidden, scroll works via mouse/touch */
            <div
              className="flex-1 min-h-0 tool-panel-scroll"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                padding: "14px 16px",
              } as React.CSSProperties}
            >
              <ErrorBoundary scope="tool" label={activeTool ? TOOL_NAMES[activeTool] : undefined}>
                <Suspense
                  key={`${activeTool}-${convertSlug ?? ""}-${toolKey}`}
                  fallback={<ToolSkeleton />}
                >
                  {ToolComponent && <ToolComponent />}
                </Suspense>
              </ErrorBoundary>
              {/* Crawlable SEO content + per-page structured data. Conversion pages
                  get the per-pair article; normal tool pages get the tool article. */}
              {convertSlug
                ? <ConvertSeoContent slug={convertSlug} />
                : activeTool && <ToolSeoContent toolId={activeTool} />}
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom nav ── */}
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {[
          { key: "studio",   label: "Studio 3D",       action: () => navigate("/visualize"), hint: "Full 3D Map Studio" },
          { key: "blog",     label: "Blog",            action: () => navigate("/blog"),      hint: "Tutorial & Panduan GIS" },
          { key: "faq",      label: t("nav.faq"),      action: onOpenFAQ,      hint: "Common questions about Shp.Aid" },
          { key: "about",    label: t("nav.about"),    action: onOpenAbout,    hint: "App info, tech stack & credits" },
          { key: "help",     label: t("nav.help"),     action: onOpenFeedback, hint: t("nav.helpHint") },
        ].map(({ key, label, action, hint }) => (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <button
                onClick={action}
                className="transition-opacity hover:opacity-80 active:opacity-60"
                style={{
                  position: "relative",
                  padding: "5px 13px",
                  borderRadius: "var(--panel-radius)",
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  background: "hsl(var(--brand-strong))",
                  color: "hsl(var(--brand-fg))",
                }}
              >
                {label}
                {key === "help" && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: -2,
                      right: -2,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "hsl(0 84% 60%)",
                      border: "1.5px solid hsl(var(--background))",
                    }}
                  />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">{hint}</TooltipContent>
          </Tooltip>
        ))}

        {/* Blog link - internal link from the highest-authority page to the
            content hub (SEO), and a real crawlable <a href="/blog">. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/blog"
              className="transition-opacity hover:opacity-80 active:opacity-60"
              style={{
                padding: "5px 13px",
                borderRadius: "var(--panel-radius)",
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                background: "transparent",
                border: "1px solid hsl(var(--brand))",
                color: "hsl(var(--brand-strong))",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {t("nav.blog")}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="top">{t("nav.blogHint")}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default LeftPanel;
